import ChangedField from "./ChangedField";

import LocationIcon from "../../../assets/location-pin.svg?react";
import * as symbolUtils from "@arcgis/core/symbols/support/symbolUtils";
import "./EditedFeaturePanel.css";
import Collapsible from "../../genericComponents/Collapsible/Collapsible";
import { GetCodedValueAndAlias, getLayerName, isMainFeatureByLayerId, zoomTo } from "../../../utils/arcgisUtils";
import { useContext, useEffect, useState } from "react";
import {
    addGraphicsToJobsGraphicsLayer,
    objectShouldValidate,
    objectsShouldValidate,
    removeGraphicsFromJobsGraphicsLayer,
} from "../../../utils/jobsUtils";
import { AppContext, JobContext } from "../../../Context";
import { LeftPanelTabs } from "../../../constants/Panels";
import { setLeftPanelActiveTab } from "../../../store/appSlice";
import { useDispatch } from "react-redux";
import { FeatureObject, FeatureWithChanges } from "../../../utils/newVersioningUtils";
import ValidationResult from "./ValidationResult";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface EditedFeaturePanelForPlanProps {
    editedFeature: FeatureWithChanges;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    clearHighlightedFeatures: () => void;
}

const EditedFeaturePanelForPlan = (props: EditedFeaturePanelForPlanProps) => {
    const dispatch = useDispatch();

    const [symbolHtmlElement, setSymbolHtmlElement] = useState<HTMLElement | null>(null);
    const [isOriginalFeatureVisible, setIsOriginalFeatureVisible] = useState(false);
    const appContext = useContext(AppContext);
    const jobContext = useContext(JobContext);
    const validateObjects =
        objectsShouldValidate(jobContext?.selectedJob.value?.attributes.jobb_type) &&
        objectShouldValidate(props.editedFeature);

    {
        /* Note 31.10.2025 - Leaving, but commented out for a month in case KYV changes their mind again. */
    }
    // const [objectWorkOrderIds, setObjectWorkOrderIds] = useState<string[]>([]);
    // const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);
    // const isSynchronizingToFDV = useSelector((state: StoreState) => state.app.isSynchronizingToFDV);

    const getTitle = () => {
        let sjomerke_nr = "";
        for (const key in props.editedFeature.mainFeature.versionGraphic?.attributes) {
            if (key === "sjomerke_nr") {
                sjomerke_nr = props.editedFeature.mainFeature.versionGraphic?.attributes[key];
                break;
            }
        }
        if (sjomerke_nr === "" || sjomerke_nr === null) {
            sjomerke_nr =
                props.editedFeature.mainFeature.versionGraphic?.attributes["navn"] ??
                "ObjId:" +
                    props.editedFeature.mainFeature.objectId +
                    " - " +
                    getLayerName(props.editedFeature.mainFeature.layerId);
        }

        return sjomerke_nr;
    };

    const getChanges = () => {
        const changes: React.JSX.Element[] = [];

        if (props.editedFeature.mainFeature.type === "update" || props.editedFeature.mainFeature.type === "insert") {
            for (const field of props.editedFeature.mainFeature.changedFieldsDefault ?? []) {
                changes.push(
                    <ChangedField
                        editedFeature={props.editedFeature.mainFeature}
                        field={field}
                        fromOperative={false}
                        key={field}
                    />
                );
            }
        }

        return changes;
    };

    const getRelatedFeatures = () => {
        const relatedChanges: React.JSX.Element[] = [];
        const changesByLayer: { [key: number]: FeatureObject[] } = {};
        for (const relatedFeature of props.editedFeature.relatedFeatures) {
            if (relatedFeature.type === "update" || relatedFeature.type === "insert") {
                if (!changesByLayer[relatedFeature.layerId]) {
                    changesByLayer[relatedFeature.layerId] = [];
                }
                changesByLayer[relatedFeature.layerId].push(relatedFeature);
            }
        }

        const mainFeatureElements: React.JSX.Element[] = [];
        const relatedFeatureElements: React.JSX.Element[] = [];

        for (const layerId in changesByLayer) {
            if (isMainFeatureByLayerId(Number(layerId))) {
                const layerName = getLayerName(Number.parseInt(layerId));

                for (const mainFeature of changesByLayer[layerId]) {
                    const collapsible = (
                        <Collapsible
                            isOpen={false}
                            setIsOpen={() => {}}
                            title={
                                layerName +
                                " - " +
                                (mainFeature.versionGraphic?.attributes.sjomerke_nr ??
                                    "ObjId:" + mainFeature.versionGraphic?.attributes.objectid)
                            }
                            messageWhenEmpty="Ingen endringer funnet"
                        >
                            {mainFeature.changedFieldsDefault?.map((field) => (
                                <ChangedField
                                    key={`${mainFeature.layerId}-${mainFeature.objectId}-related-${field}`}
                                    editedFeature={mainFeature}
                                    field={field}
                                    fromOperative={false}
                                />
                            ))}
                        </Collapsible>
                    );
                    mainFeatureElements.push(collapsible);
                }
            } else {
                relatedChanges.push(
                    <div className="flex-row w-100 space-between" key={`${layerId}-related-layer`}>
                        <span className="job-card-key">{getLayerName(Number.parseInt(layerId))}</span>
                        {/* <span className="job-card-value bold">
                            Antall: {changesByLayer[layerId].length}
                        </span> */}
                    </div>
                );
            }
        }
        if (relatedFeatureElements.length > 0) {
            relatedChanges.push(...relatedFeatureElements);
        }
        if (mainFeatureElements.length > 0) {
            relatedChanges.push(...mainFeatureElements);
        }

        if (relatedChanges.length === 0) {
            return null;
        } else {
            return (
                <Collapsible
                    isOpen={false}
                    setIsOpen={() => {}}
                    title="Relaterte objekter"
                    className="edited-feature-panel-collapsible"
                    messageWhenEmpty="Ingen endringer funnet"
                >
                    {relatedChanges}
                </Collapsible>
            );
        }
    };

    const zoomToFeature = async () => {
        if (!props.editedFeature) return;

        const mainFeature = props.editedFeature.mainFeature.versionGraphic;
        if (!mainFeature) return;

        props.clearHighlightedFeatures();
        zoomTo(mainFeature, props.storeHighlightHandle, "temporary");

        appContext?.shownFeature.set(mainFeature);

        dispatch(setLeftPanelActiveTab(LeftPanelTabs.VIEWER));
    };

    const showHideFeature = () => {
        if (isOriginalFeatureVisible) {
            setIsOriginalFeatureVisible(false);
            const graphicsToRemove: Graphic[] = [];

            if (props.editedFeature.mainFeature.defaultGraphic) {
                graphicsToRemove.push(props.editedFeature.mainFeature.defaultGraphic);
            }
            for (const relatedFeature of props.editedFeature.relatedFeatures) {
                if (relatedFeature.defaultGraphic) {
                    graphicsToRemove.push(relatedFeature.defaultGraphic);
                }
            }
            removeGraphicsFromJobsGraphicsLayer(graphicsToRemove);
        } else {
            const graphicsToAdd: Graphic[] = [];
            if (props.editedFeature.mainFeature.defaultGraphic) {
                graphicsToAdd.push(props.editedFeature.mainFeature.defaultGraphic);
            } else {
                console.warn("No original graphic found for edited feature.");
            }
            for (const relatedFeature of props.editedFeature.relatedFeatures) {
                if (relatedFeature.defaultGraphic) {
                    graphicsToAdd.push(relatedFeature.defaultGraphic);
                }
            }
            addGraphicsToJobsGraphicsLayer(graphicsToAdd);
            setIsOriginalFeatureVisible(true);
        }
    };

    // const restoreObject = () => {
    //     const nauticalObject = { mainFeature: props.editedFeature, relatedFeatures: props.relatedFeatures };
    //     props.restoreFeature(props.layerId, nauticalObject);
    // };

    useEffect(() => {
        return () => {
            if (isOriginalFeatureVisible) {
                showHideFeature();
            }
        };
    }, [isOriginalFeatureVisible]);

    useEffect(() => {
        if (props.editedFeature.mainFeature.versionGraphic) {
            symbolUtils.getDisplayedSymbol(props.editedFeature.mainFeature.versionGraphic).then((symbol) => {
                if (symbol) {
                    symbolUtils.renderPreviewHTML(symbol, { size: 12 }).then((symbolElement) => {
                        if (!symbolElement) return;
                        setSymbolHtmlElement(symbolElement);
                    });
                } else {
                    setSymbolHtmlElement(null);
                }
            });
        } else {
            setSymbolHtmlElement(null);
        }

        {
            /* Note 31.10.2025 - Leaving, but commented out for a month in case KYV changes their mind again. */
        }
        // const jobObjectId = jobsContext?.selectedJob.value?.attributes.objectid;
        // if (!jobObjectId) {
        //     setObjectWorkOrderIds([]);
        //     return;
        // }
        // getJobWorkOrdersFromJob(jobObjectId).then((workOrders) => {
        //     if (workOrders.length > 0) {
        //         const featureLayerId = props.editedFeature.mainFeature.layerId;
        //         const featureGlobalId = props.editedFeature.mainFeature.versionGraphic?.attributes.globalid;
        //         const matchingWorkOrders = workOrders.filter(wo => wo.layerId === featureLayerId && wo.globalId === featureGlobalId);
        //         setObjectWorkOrderIds(matchingWorkOrders.map(wo => wo.workOrderId));
        //     } else {
        //         setObjectWorkOrderIds([]);
        //     }
        // });
    }, [props.editedFeature]);

    return (
        <div className="edited-feature-panel">
            <div className="edited-feature-panel-header">
                <div
                    className="search-item-symbol"
                    dangerouslySetInnerHTML={{ __html: symbolHtmlElement?.outerHTML ?? "" }}
                ></div>
                <button
                    className="nfs-button no-flex fit-content outline"
                    style={{ padding: "0rem 0.5rem" }}
                    onClick={showHideFeature}
                >
                    {isOriginalFeatureVisible ? "Skjul" : "Vis"}
                </button>
                <span>{getTitle()}</span>
                <button className="empty-button" onClick={zoomToFeature}>
                    <LocationIcon />
                </button>
            </div>
            <div className="flex-row w-100 space-between">
                <span className="job-card-key">Status</span>
                <span className="job-card-value bold">
                    {
                        GetCodedValueAndAlias(
                            props.editedFeature.mainFeature.versionGraphic?.layer,
                            "nfs_status",
                            props.editedFeature.mainFeature.versionGraphic?.attributes.nfs_status
                        )[1]
                    }
                </span>
            </div>
            <div className="flex-row w-100 space-between">
                <span className="job-card-key">Navn</span>
                <span className="job-card-value bold">
                    {props.editedFeature.mainFeature.versionGraphic?.attributes.navn}
                </span>
            </div>
            <div className="flex-row w-100 space-between">
                <span className="job-card-key">Sted</span>
                <span className="job-card-value bold">
                    {props.editedFeature.mainFeature.versionGraphic?.attributes.sted}
                </span>
            </div>

            {validateObjects && <ValidationResult validationResult={props.editedFeature.validationResult} />}

            {/* Note 31.10.2025 - Leaving, but commented out for a month in case KYV changes their mind again. */}
            {/* <div className="flex-row w-100 space-between">
                <span className="job-card-key">Arbeidsordre</span>
                <span className="job-card-value bold">
                    {objectWorkOrderIds.map((id) => (
                        <a
                            key={id}
                            href={`${AppConfig.FDV.BaseUrl}${AppConfig.FDV.WorkOrderBase}${id}`}
                            target="_blank"
                            rel="noopener"
                            className="fit-content empty-button center"
                        >
                            {id}
                            <OpenInNewWindowIcon />
                        </a>
                    ))}
                    {!objectWorkOrderIds.length &&
                        <NfsButton
                            disabled={isSynchronizingToFDV}
                            onClick={async () => {
                                dispatch(setIsSynchronizingToFDV(true));
                                const workorderIds = await createWorkOrderForJob(jobsContext?.selectedJob.value?.attributes.objectid, jobsContext?.selectedJob.value?.attributes.navn ?? "", [props.editedFeature], activeVersion?.versionIdentifier.name ?? "");
                                if (workorderIds.length > 0) {
                                    setObjectWorkOrderIds((prev) => [...prev, ...workorderIds]);
                                }
                                dispatch(setIsSynchronizingToFDV(false));
                            }}
                        >
                            Opprett arbeidsordre
                            {isSynchronizingToFDV && <Loader inline size={24} />}
                        </NfsButton>
                    }
                </span>
            </div> */}
            <br />
            <span>Viser endringer sammenlignet med Planroot</span>
            <Collapsible
                isOpen={false}
                setIsOpen={() => {}}
                title="Vis endringer"
                className="edited-feature-panel-collapsible"
                messageWhenEmpty="Ingen endringer funnet"
            >
                {getChanges()}
            </Collapsible>
            {getRelatedFeatures()}
        </div>
    );
};

export default EditedFeaturePanelForPlan;
